import React, { useEffect, useMemo, useState } from 'react';
import { Select, InputNumber, DatePicker, Button, Slider } from 'antd';
import { resolveTrackColor } from '../utils/dashboard/trackColorHelpers';

function pointTimeMs(p) {
  if (!p) return null;
  const raw = p.timestamp || p.time || p.located_at || p.created_at || p.timeIso;
  if (raw == null) return null;
  const d = new Date(raw);
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

function formatClock(ms) {
  if (ms == null || !Number.isFinite(ms)) return '--:--:--';
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatShort(ms) {
  if (ms == null || !Number.isFinite(ms)) return '--';
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function memberInViewport(member, map) {
  if (!map || !member) return true;
  try {
    const lng = Number(member.last_longitude ?? member.longitude ?? member.lng);
    const lat = Number(member.last_latitude ?? member.latitude ?? member.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
    const b = map.getBounds?.();
    if (!b) return true;
    return b.contains([lng, lat]);
  } catch (_) {
    return true;
  }
}

/**
 * 指挥页轨迹模式 + 回放控件（对齐安卓 Locus 类回放能力：多选目标/色块/视口过滤/时间轴/速度）
 */
export default function TrackModePlaybackControls({
  trackMode,
  trackMinutes,
  trackHours,
  trackDays,
  playbackRange,
  isPlaying,
  onTrackModeChange,
  onTrackMinutesChange,
  onTrackHoursChange,
  onTrackDaysChange,
  onPlaybackRangeChange,
  onApplyTrackMode,
  onStartPlayback,
  onPausePlayback,
  onStopPlayback,
  /** 回放会话中（播放或暂停）；idle/播完/停止后为 false，用于隐藏「停止回放」 */
  playbackSessionActive = false,
  /** 紧凑：仅模式+时间（显示设置里）；完整：回放标签面板 */
  variant = 'full',
  members = [],
  trackData = {},
  trackColors = {},
  playbackTargetNames = [],
  onPlaybackTargetNamesChange,
  playbackSpeed = 10,
  onPlaybackSpeedChange,
  playbackIndex = 0,
  onPlaybackIndexChange,
  getMap,
  showTracks = {},
  onToggleTrack
}) {
  const [viewportOnly, setViewportOnly] = useState(false);
  const [viewportTick, setViewportTick] = useState(0);

  useEffect(() => {
    if (!viewportOnly) return undefined;
    const t = setInterval(() => setViewportTick((v) => v + 1), 1500);
    return () => clearInterval(t);
  }, [viewportOnly]);

  const roster = useMemo(() => {
    const list = Array.isArray(members) ? members.slice() : [];
    list.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'zh'));
    return list;
  }, [members]);

  const map = typeof getMap === 'function' ? getMap() : null;
  void viewportTick;

  const visibleRoster = useMemo(() => {
    if (!viewportOnly) return roster;
    return roster.filter((m) => memberInViewport(m, map));
  }, [roster, viewportOnly, map, viewportTick]);

  const selectedSet = useMemo(
    () => new Set(Array.isArray(playbackTargetNames) ? playbackTargetNames : []),
    [playbackTargetNames]
  );

  const timeline = useMemo(() => {
    const names = Array.from(selectedSet);
    let minT = Infinity;
    let maxT = -Infinity;
    let totalPts = 0;
    names.forEach((name) => {
      const track = trackData?.[name] || [];
      totalPts += track.length;
      track.forEach((p) => {
        const t = pointTimeMs(p);
        if (t == null) return;
        if (t < minT) minT = t;
        if (t > maxT) maxT = t;
      });
    });
    if (!Number.isFinite(minT) || !Number.isFinite(maxT) || maxT <= minT) {
      const start = playbackRange?.start?.valueOf?.() ?? playbackRange?.start;
      const end = playbackRange?.end?.valueOf?.() ?? playbackRange?.end;
      const s = start != null ? Number(new Date(start).getTime()) : null;
      const e = end != null ? Number(new Date(end).getTime()) : null;
      if (s != null && e != null && e > s) {
        return { minT: s, maxT: e, totalPts, span: e - s };
      }
      return { minT: null, maxT: null, totalPts, span: 0 };
    }
    return { minT, maxT, totalPts, span: maxT - minT };
  }, [selectedSet, trackData, playbackRange]);

  const primaryTrack = useMemo(() => {
    let best = null;
    selectedSet.forEach((name) => {
      const track = trackData?.[name] || [];
      if (!best || track.length > best.track.length) best = { name, track };
    });
    return best;
  }, [selectedSet, trackData]);

  const currentMs = useMemo(() => {
    if (primaryTrack?.track?.length) {
      const idx = Math.min(Math.max(0, playbackIndex || 0), primaryTrack.track.length - 1);
      const t = pointTimeMs(primaryTrack.track[idx]);
      if (t != null) return t;
      if (timeline.minT != null && timeline.span > 0 && primaryTrack.track.length > 1) {
        return timeline.minT + (idx / (primaryTrack.track.length - 1)) * timeline.span;
      }
    }
    return timeline.minT;
  }, [primaryTrack, playbackIndex, timeline]);

  const setSelectedNames = (names) => {
    onPlaybackTargetNamesChange?.(names);
  };

  const toggleName = (name) => {
    if (!name) return;
    const next = new Set(selectedSet);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelectedNames(Array.from(next));
  };

  const selectAllVisible = () => {
    setSelectedNames(visibleRoster.map((m) => m.name).filter(Boolean));
  };

  const clearSelected = () => setSelectedNames([]);

  const onTimelineChange = (ratio01) => {
    if (!primaryTrack?.track?.length) return;
    const maxIdx = primaryTrack.track.length - 1;
    const idx = Math.round(Math.min(1, Math.max(0, ratio01)) * maxIdx);
    onPlaybackIndexChange?.(idx);
  };

  const timelineValue = useMemo(() => {
    if (!primaryTrack?.track?.length) return 0;
    const maxIdx = primaryTrack.track.length - 1;
    if (maxIdx <= 0) return 0;
    return Math.min(1, Math.max(0, (playbackIndex || 0) / maxIdx));
  }, [primaryTrack, playbackIndex]);

  const ensureTracksVisible = () => {
    selectedSet.forEach((name) => {
      if (name && !showTracks?.[name] && onToggleTrack) onToggleTrack(name);
    });
  };

  const handlePlayClick = () => {
    if (isPlaying) {
      // 暂停：保留进度；勿走停止归零
      (onPausePlayback || onStopPlayback)?.();
      return;
    }
    if (selectedSet.size < 1) return;
    ensureTracksVisible();
    onStartPlayback?.();
  };

  const handleStopClick = () => {
    onStopPlayback?.();
  };

  const hasPlaybackTarget = selectedSet.size > 0;
  // 未选目标时禁用「播放」；播放/暂停中若清空勾选 → 停止会话（最稳妥）
  const playDisabled = !isPlaying && !hasPlaybackTarget;

  useEffect(() => {
    if (hasPlaybackTarget) return;
    if (!playbackSessionActive) return;
    onStopPlayback?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅随勾选/会话变化
  }, [hasPlaybackTarget, playbackSessionActive]);

  const modeRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, minWidth: 54 }}>轨迹模式:</span>
      <Select
        value={trackMode}
        onChange={onTrackModeChange}
        style={{ width: 120, height: 22, fontSize: 12 }}
        size="small"
        dropdownStyle={{ fontSize: 12, minWidth: 120 }}
      >
        <Select.Option value="recent">最近N分钟</Select.Option>
        <Select.Option value="recent_hours">最近N小时</Select.Option>
        <Select.Option value="recent_days">最近N天</Select.Option>
        <Select.Option value="all">全部历史</Select.Option>
      </Select>
      {trackMode === 'recent' && (
        <InputNumber min={0} max={1440} value={trackMinutes} onChange={onTrackMinutesChange} style={{ width: 48, height: 22, fontSize: 12 }} size="small" />
      )}
      {trackMode === 'recent_hours' && (
        <InputNumber min={0} max={24} value={trackHours} onChange={onTrackHoursChange} style={{ width: 48, height: 22, fontSize: 12 }} size="small" />
      )}
      {trackMode === 'recent_days' && (
        <InputNumber min={0} max={3650} value={trackDays} onChange={onTrackDaysChange} style={{ width: 48, height: 22, fontSize: 12 }} size="small" />
      )}
      <Button
        onClick={onApplyTrackMode}
        size="small"
        style={{ padding: '0 6px', height: 22, fontSize: 12, marginLeft: 2, display: 'flex', alignItems: 'center' }}
      >
        应用
      </Button>
    </div>
  );

  const rangeRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, minWidth: 54 }}>时间范围:</span>
      <DatePicker
        showTime
        value={playbackRange?.start}
        onChange={(v) => onPlaybackRangeChange?.({ ...playbackRange, start: v })}
        size="small"
        style={{ width: 110, height: 22, fontSize: 12 }}
        inputReadOnly={false}
        placeholder="开始时间"
      />
      <DatePicker
        showTime
        value={playbackRange?.end}
        onChange={(v) => onPlaybackRangeChange?.({ ...playbackRange, end: v })}
        size="small"
        style={{ width: 110, height: 22, fontSize: 12 }}
        inputReadOnly={false}
        placeholder="结束时间"
      />
      {variant !== 'full' && (
        <>
          <Button onClick={onStartPlayback} size="small" style={{ padding: '0 8px', height: 22, fontSize: 12, marginLeft: 2 }}>
            开始回放
          </Button>
          {isPlaying && (
            <Button onClick={onStopPlayback} size="small" style={{ padding: '0 8px', height: 22, fontSize: 12, marginLeft: 2 }}>
              停止
            </Button>
          )}
        </>
      )}
    </div>
  );

  if (variant !== 'full') {
    return (
      <div style={{ margin: '2px 0', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {modeRow}
        {rangeRow}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {modeRow}
      {rangeRow}

      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
          gap: 8,
          flexWrap: 'wrap'
        }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1976d2' }}>回放目标（可多选）</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={selectAllVisible} style={linkBtn}>全选</button>
            <button type="button" onClick={clearSelected} style={linkBtn}>清空</button>
          </span>
        </div>
        <div style={{
          maxHeight: 200,
          overflowY: 'auto',
          border: '1px solid #eef2f6',
          borderRadius: 8,
          padding: '4px 8px'
        }}
        >
          {visibleRoster.length === 0 ? (
            <div style={{ fontSize: 12, color: '#999', padding: 8 }}>暂无队员</div>
          ) : (
            visibleRoster.map((m) => {
              const name = m.name;
              const color = resolveTrackColor(name, trackColors, { name, members: roster });
              const pts = (trackData?.[name] || []).length;
              return (
                <label
                  key={name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 0',
                    fontSize: 12,
                    cursor: 'pointer',
                    color: '#333'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedSet.has(name)}
                    onChange={() => toggleName(name)}
                  />
                  <span
                    title="轨迹色"
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: color,
                      flexShrink: 0,
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.08)'
                    }}
                  />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                  </span>
                  <span style={{ color: '#8a93a0', fontSize: 11 }}>{pts || '—'}</span>
                </label>
              );
            })
          )}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 8,
          gap: 8,
          flexWrap: 'wrap',
          fontSize: 12,
          color: '#555'
        }}
        >
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={viewportOnly}
              onChange={(e) => setViewportOnly(e.target.checked)}
            />
            仅视口内
          </label>
          <span style={{ color: '#8a93a0' }}>
            视口内 {visibleRoster.length} / 共 {roster.length}
          </span>
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8a93a0', marginBottom: 2 }}>
          <span>{formatShort(timeline.minT)}</span>
          <span>{formatShort(timeline.maxT)}</span>
        </div>
        <Slider
          min={0}
          max={1000}
          tipFormatter={null}
          value={Math.round(timelineValue * 1000)}
          onChange={(v) => onTimelineChange((Number(v) || 0) / 1000)}
          disabled={!primaryTrack?.track?.length}
        />
        <div style={{ textAlign: 'center', fontSize: 18, fontWeight: 600, color: '#1976d2', letterSpacing: 1 }}>
          {formatClock(currentMs)}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={handlePlayClick}
          disabled={playDisabled}
          title={playDisabled ? '请先勾选回放目标' : undefined}
          style={{
            flex: 1,
            height: 36,
            borderRadius: 8,
            border: 'none',
            background: playDisabled ? '#c5cad3' : (isPlaying ? '#ff9800' : '#1976d2'),
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: playDisabled ? 'not-allowed' : 'pointer',
            opacity: playDisabled ? 0.85 : 1
          }}
        >
          {isPlaying ? '暂停' : '播放'}
        </button>
        {playbackSessionActive ? (
          <button
            type="button"
            onClick={handleStopClick}
            title="停止回放并回到起点，下次播放从头开始"
            style={{
              height: 36,
              minWidth: 88,
              borderRadius: 8,
              border: '1px solid #cfd8dc',
              background: '#fff',
              color: '#546e7a',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '0 10px'
            }}
          >
            停止回放
          </button>
        ) : null}
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#555', marginBottom: 2 }}>
          <span>速度</span>
          <span style={{ fontWeight: 600, color: '#1976d2' }}>{playbackSpeed || 1}X</span>
        </div>
        <Slider
          min={1}
          max={20}
          step={1}
          value={Number(playbackSpeed) || 1}
          onChange={(v) => onPlaybackSpeedChange?.(Number(v) || 1)}
          marks={{ 1: '1X', 5: '5X', 10: '10X', 20: '20X' }}
        />
      </div>

      <div style={{ fontSize: 11, color: '#8a93a0' }}>
        已选 {selectedSet.size} 人
        {timeline.totalPts > 0 ? ` · ${timeline.totalPts} 采样点` : ''}
        {primaryTrack ? ` · 主轨 ${primaryTrack.name}` : ''}
      </div>
    </div>
  );
}

const linkBtn = {
  border: 'none',
  background: 'transparent',
  color: '#1976d2',
  cursor: 'pointer',
  fontSize: 12,
  padding: 0
};